import { useState, useRef, useEffect, useCallback, useLayoutEffect, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import styles from './EnumValueSelect.module.css';

interface EnumValueOption {
  key: string;
  value: string;
}

interface EnumValueSelectProps {
  options: EnumValueOption[];
  value: string;
  onChange: (value: string) => void;
  clearLabel: string;
  disabled?: boolean;
}

export default function EnumValueSelect({
  options,
  value,
  onChange,
  clearLabel,
  disabled = false,
}: EnumValueSelectProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties | null>(null);

  const selected = options.find(o => o.value === value);

  const updateDropdownPosition = useCallback(() => {
    if (!wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const viewportPadding = 8;
    const dropdownGap = 4;
    const maxMenuHeight = 220;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const openUpwards = spaceBelow < 160 && spaceAbove > spaceBelow;
    const availableHeight = Math.max(
      120,
      Math.min(maxMenuHeight, (openUpwards ? spaceAbove : spaceBelow) - dropdownGap)
    );

    setDropdownStyle(openUpwards
      ? {
        position: 'fixed',
        left: rect.left,
        bottom: window.innerHeight - rect.top + dropdownGap,
        width: rect.width,
        maxHeight: availableHeight,
        zIndex: 1000,
      }
      : {
        position: 'fixed',
        top: rect.bottom + dropdownGap,
        left: rect.left,
        width: rect.width,
        maxHeight: availableHeight,
        zIndex: 1000,
      });
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const clickedInsideWrapper = !!wrapperRef.current?.contains(target);
      const clickedInsideDropdown = !!dropdownRef.current?.contains(target);

      if (!clickedInsideWrapper && !clickedInsideDropdown) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;

    updateDropdownPosition();

    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);

    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [open, updateDropdownPosition]);

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        type="button"
        className={styles.trigger}
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
      >
        <span className={styles.triggerLeft}>
          {selected ? (
            <>
              <span className={styles.triggerName}>{selected.key}</span>
              {selected.value !== selected.key && (
                <span className={styles.valueBadge}>{selected.value}</span>
              )}
            </>
          ) : (
            <span className={styles.placeholder}>{clearLabel}</span>
          )}
        </span>
        <ChevronDown size={16} className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} />
      </button>

      {open && dropdownStyle && createPortal(
        <div className={styles.dropdown} style={dropdownStyle} ref={dropdownRef}>
          <div
            className={`${styles.option} ${!selected ? styles.optionSelected : ''}`}
            onClick={() => { onChange(''); setOpen(false); }}
          >
            <span className={styles.optionPlaceholder}>{clearLabel}</span>
          </div>
          {options.map(opt => (
            <div
              key={opt.key}
              className={`${styles.option} ${opt.value === value ? styles.optionSelected : ''}`}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              <span className={styles.optionName}>{opt.key}</span>
              {opt.value !== opt.key && (
                <span className={styles.valueBadge}>{opt.value}</span>
              )}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
